<template>
  <div>
    <h1 :class="$style.title">{{ t('favourites.page.heading') }} ({{ listingsCount }})</h1>
    <div v-if="isFavouritesInitialized">
      <div :class="$style.topNotificationContainer">
        <HgNotificationMessage
          v-if="isOnboardingMessageVisible"
          :class="$style.onboardingMessage"
          :showCloseButton="isUserAuthenticated"
          theme="info"
          @close="closeOnboardingMessage"
        >
          <strong>
            {{
              isUserAuthenticated
                ? t('favourites.page.crossDevice.loggedin.title')
                : t('favourites.page.crossDevice.login.title')
            }}:
          </strong>
          <div :class="$style.messageParagraph">
            {{
              isUserAuthenticated
                ? t('favourites.page.crossDevice.loggedin.message')
                : t('favourites.page.crossDevice.login.message')
            }}
          </div>
          <a v-if="!isUserAuthenticated" class="signInLink" @click="handleSignIn">
            <HgButton theme="secondary-bordered">
              {{ t('favourites.page.crossDevice.login.button') }}
            </HgButton>
          </a>
        </HgNotificationMessage>

        <HgNotificationMessage
          v-if="isUnavailableListingsBannerVisible"
          :class="$style.unavailableListingsMessage"
          :showCloseButton="!isDismissingUnavailableBanner"
          theme="danger"
          @close="dismissUnavailableListingsBanner"
        >
          {{
            t('favourites.page.noLongerAvailableMessage', {
              count: unavailableListingsCount,
            })
          }}
        </HgNotificationMessage>
      </div>

      <template v-if="shouldShowEmptyListPlaceholder">
        <EmptyListPlaceholder :class="$style.emptyListPlaceholder" />
        <PropertyOwnershipCard
          v-if="shouldShowPropertyCard"
          :class="$style.listingCard"
          :mode="shouldShowHomeownerWorldCta ? 'cta' : 'question'"
          :isSaving="isPropertyOwnershipCardBusy"
          :learnMoreUrl="resolvedLearnMoreUrl"
          @select="handleOwnershipAnswer"
          @addProperty="handleHomeownerWorldCtaClick"
          @learnMore="handleLearnMoreClick"
        />
      </template>

      <template v-else-if="shouldShowFavourites">
        <template v-for="(record, index) in favouriteListingRecords" :key="record.listing.id">
          <ListItem
            :class="$style.listingCard"
            :listingItem="record"
            :listingVisited="false"
            :listingPosition="index"
            @item-clicked="trackListItemClick(record.listing.id, index)"
          />
          <PropertyOwnershipCard
            v-if="index === 0 && shouldShowPropertyCard"
            :class="$style.listingCard"
            :mode="shouldShowHomeownerWorldCta ? 'cta' : 'question'"
            :isSaving="isPropertyOwnershipCardBusy"
            :learnMoreUrl="resolvedLearnMoreUrl"
            @select="handleOwnershipAnswer"
            @addProperty="handleHomeownerWorldCtaClick"
            @learnMore="handleLearnMoreClick"
          />
        </template>
      </template>

      <HgToast :isVisible="isFavouriteRemovedToastVisible">
        <HgNotificationMessage :title="t('favourites.page.favouriteRemoved')" theme="success">
          <p>
            <a href="#" @click.prevent="revertLastFavouriteRemoval">
              {{ t('favourites.page.undo') }}
            </a>
          </p>
        </HgNotificationMessage>
      </HgToast>

      <HgToast :isVisible="ownershipConfirmationToastVisible">
        <HgNotificationMessage
          :showCloseButton="true"
          :title="t('favourites.page.propertyOwnership.confirmationTitle')"
          theme="success"
          @close="dismissOwnershipConfirmationToast"
        >
          <p>
            {{ t('favourites.page.propertyOwnership.confirmationMessage') }}
            <HgLink :href="accountSettingsUrl" target="_blank" rel="noopener noreferrer">
              {{ t('favourites.page.propertyOwnership.accountSettingsLink') }} </HgLink
            >.
          </p>
        </HgNotificationMessage>
      </HgToast>
    </div>
  </div>
</template>

<script lang="ts" setup>
import EmptyListPlaceholder from '@/components/Favourites/EmptyListPlaceholder/EmptyListPlaceholder.vue';
import PropertyOwnershipCard from '@/components/Favourites/PropertyOwnershipCard/PropertyOwnershipCard.vue';
import ListItem from '@/components/ListItem/ListItem.vue';
import { usePropertyOwnership } from '@/composables/favourites/usePropertyOwnership';
import { loginWithRedirect } from '@/helpers/Authentication/authClient';
import { getCurrentPlatform } from '@/helpers/envService';
import {
  GTMPageType,
  InteractionContainerName,
  InteractionEvent,
  InteractionGoal,
  trackEvent,
  trackVirtualPageView,
} from '@/helpers/GTM/GTMHelper';
import {
  HgButton,
  HgLink,
  HgNotificationMessage,
  HgSupportedLanguage,
  HgToast,
} from '@smg-real-estate/domus';
import { useHead } from '@unhead/vue';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const { t, locale } = useI18n();
const store = useStore();
const {
  accountSettingsUrl,
  dismissOwnershipConfirmationToast,
  handleOwnershipAnswer,
  handleHomeownerWorldCtaClick,
  handleLearnMoreClick,
  isOwnershipConfirmationToastVisible,
  isSavingAddProperty,
  isSavingOwnershipAnswer,
  learnMoreUrl,
  shouldShowPropertyCard,
  shouldShowHomeownerWorldCta,
} = usePropertyOwnership();

const isPropertyOwnershipCardBusy = computed(() =>
  shouldShowHomeownerWorldCta.value ? isSavingAddProperty.value : isSavingOwnershipAnswer.value,
);

const resolvedLearnMoreUrl = computed((): string => learnMoreUrl.value);
const ownershipConfirmationToastVisible = computed(
  (): boolean => isOwnershipConfirmationToastVisible.value,
);

const unavailableListingsCount = ref(0);
const isDismissingUnavailableBanner = ref(false);
const isFavouriteRemovedToastVisible = ref(false);

const isUserAuthenticated = computed(() => store.getters.isUserAuthenticated);
const shouldShowEmptyListPlaceholder = computed(
  () => store.getters.hasFetchedFavourites && store.getters.favouriteListingRecords.length === 0,
);
const shouldShowFavourites = computed(
  () => store.getters.hasFetchedFavourites && store.getters.favouriteListingRecords.length > 0,
);
const isFavouritesInitialized = computed(() => store.getters.isFavouritesInitialized);
const isOnboardingMessageVisible = computed(
  () => !store.state.favourites.isFavouritesOnboardingMessageClosed,
);
const favouriteListingRecords = computed(() => store.getters.favouriteListingRecords);
const lastRemovedFavouriteListingDescriptor = computed(
  () => store.state.favourites.lastRemovedListingDescriptor,
);
const currentLocale = computed(() => locale.value);
const listingsCount = computed(() => favouriteListingRecords.value.length);
const hasUnavailableListings = computed(() => store.state.favourites.hasUnavailableListings);
const isUnavailableListingsBannerVisible = computed(
  () => hasUnavailableListings.value || isDismissingUnavailableBanner.value,
);

const closeOnboardingMessage = () => {
  store.dispatch('closeOnboardingMessage');
};

const fetchFavouriteListings = async () => {
  await store.dispatch('fetchFavouriteListingRecords');
  unavailableListingsCount.value = store.getters.unavailableListingsCount;
  trackFavouritesVirtualPageView();
  await removeUnavailableListings();
};

const revertLastFavouriteRemoval = async () => {
  await store.dispatch('restoreLastRemovedFavourite');
  trackEvent(InteractionEvent.UNDO, {
    h_event_category_006: InteractionContainerName.UNDO,
    h_event_action_007: InteractionGoal.UNDO_FAVOURITE_REMOVAL,
    h_event_label_008: InteractionContainerName.FAVOURITES_LIST,
  });
};

const removeUnavailableListings = async () => {
  await store.dispatch('removeUnavailableFavourites');
};

const trackFavouritesVirtualPageView = () => {
  const listingIds = favouriteListingRecords.value.map((record) => record.listing.id).join(', ');

  trackVirtualPageView({
    h_page_type_110: GTMPageType.FAVOURITES_PAGE,
    h_content_language_116: currentLocale.value as HgSupportedLanguage,
    h_list_name_086: GTMPageType.FAVOURITES_PAGE,
    h_list_items_all_087: listingIds,
    h_list_items_total_284: favouriteListingRecords.value.length,
  });
};

const trackUnavailableListingsBannerDismissed = () => {
  trackEvent(InteractionEvent.FAVOURITES_INTERACTION, {
    h_event_action_007: InteractionGoal.CLOSE_UNAVAILABLE_FAVOURITES_BANNER,
    h_event_label_008: InteractionContainerName.FAVOURITES,
  });
};

const trackListItemClick = (listingId: string, listingIndex: number) => {
  trackEvent(InteractionEvent.FAVOURITES_INTERACTION, {
    h_event_category_006: InteractionContainerName.FAVOURITES_CLICKTHROUGH,
    h_event_action_007: InteractionGoal.GO_TO_PDP,
    h_event_label_008: listingId,
    h_listing_advertisement_id_120: listingId,
    h_position_on_list_overall_283: listingIndex,
  });
};

const dismissUnavailableListingsBanner = async () => {
  if (isDismissingUnavailableBanner.value) {
    return;
  }

  isDismissingUnavailableBanner.value = true;

  try {
    const dismissSucceeded = await store.dispatch('dismissUnavailableListingsBanner');

    if (dismissSucceeded) {
      trackUnavailableListingsBannerDismissed();
    }
  } finally {
    isDismissingUnavailableBanner.value = false;
  }
};
const handleSignIn = async () => {
  await loginWithRedirect(currentLocale.value as HgSupportedLanguage);
};

useHead({
  title: t(`favourites.page.title.${getCurrentPlatform()}`),
  meta: [
    {
      name: 'robots',
      'data-vmid': 'robots',
      content: 'noindex',
    },
  ],
});

watch(
  isFavouritesInitialized,
  async (isInitialized) => {
    if (isInitialized) {
      await fetchFavouriteListings();
    }
  },
  { immediate: true },
);

watch(currentLocale, () => {
  trackFavouritesVirtualPageView();
});

watch(lastRemovedFavouriteListingDescriptor, async (newValue) => {
  isFavouriteRemovedToastVisible.value = false;

  if (!newValue) {
    return;
  }

  await nextTick();
  isFavouriteRemovedToastVisible.value = true;
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

$vertical-spacing-desktop: $space-200;
$list-placeholder-vertical-spacing-desktop: 72px;

.title {
  margin: $space-100 0;

  @include heading-1;
}

.messageParagraph {
  padding-top: $space-25;
  padding-bottom: $space-25;
}

.topNotificationContainer {
  margin-bottom: $space-100;

  &:empty {
    margin-bottom: 0;
  }

  .onboardingMessage,
  .unavailableListingsMessage {
    margin-bottom: $space-100;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.emptyListPlaceholder {
  margin: $space-400 0;
}

.listingCard {
  margin-bottom: $space-100;
  cursor: pointer;
}

@media (min-width: $viewport-md-min) {
  .title {
    margin: $vertical-spacing-desktop 0;
  }

  .topNotificationContainer {
    margin-bottom: $vertical-spacing-desktop;
  }

  .emptyListPlaceholder {
    margin: $list-placeholder-vertical-spacing-desktop 0;
  }

  .listingCard {
    margin-bottom: $vertical-spacing-desktop;
  }
}
</style>
